import { ClientTable } from "../../../components/ui/ClientTable";
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ChevronDown, ChevronUp, Funnel, RefreshCw } from "lucide-react";
import { AppLayout, PageBackLink } from "../../../components/layout";
import { PageTitleRow } from "../../../components/layout/PageTitleRow";
import { ContentCard, ui } from "../../../components/ui";
import { InventoryJournalTable } from "../components/InventoryJournalTable";
import { StockJournalFiltersModal } from "../components/StockJournalFiltersModal";
import { inventoryManagementService } from "../../../services/inventoryManagementService";
import { collectPageRecords } from "../../../lib/pagination";
import { filterJournalByRegisterType } from "../utils/registerTypeFilter";
import { cn } from "../../../lib/utils";

const JOURNAL_REGISTER_FETCH_SIZE = 50;

const formatCurrency = (val) => {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(Number(val) || 0);
};

const formatQty = (val) => {
  const n = Number(val);
  return Number.isFinite(n) ? n.toLocaleString() : "—";
};

/** Stack multi-word header labels vertically to save horizontal space. */
function StackedHeader({ label, align = "left", className }) {
  const words = String(label).trim().split(/\s+/).filter(Boolean);
  return (
    <th
      className={cn(
        "px-3 py-2 font-bold leading-tight align-bottom",
        align === "right" ? "text-right" : align === "center" ? "text-center" : "text-left",
        className
      )}
    >
      {words.length <= 1 ? (
        label
      ) : (
        <span className="inline-flex flex-col normal-case tracking-normal text-[11px]">
          {words.map((word, i) => (
            <span key={`${word}-${i}`}>{word}</span>
          ))}
        </span>
      )}
    </th>
  );
}

export function StockPositionDetailPage() {
  const { t } = useTranslation();
  const { productId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("position");

  const [product, setProduct] = useState(null);
  const [warehouseInventory, setWarehouseInventory] = useState([]);
  const [expandedWarehouses, setExpandedWarehouses] = useState({});

  const [journalData, setJournalData] = useState([]);
  const [journalTotalItems, setJournalTotalItems] = useState(0);
  const [loadingJournal, setLoadingJournal] = useState(false);
  const [journalError, setJournalError] = useState("");
  const [journalFilters, setJournalFilters] = useState({
    warehouseLocationId: "All",
    movementType: "All",
    registerType: "All",
    startDate: "",
    endDate: "",
  });
  const [journalFilterModalOpen, setJournalFilterModalOpen] = useState(false);
  const [journalPage, setJournalPage] = useState(1);
  const JOURNAL_PAGE_SIZE = 10;

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");

    inventoryManagementService
      .getStockPositionDetail(productId)
      .then((res) => {
        if (!active) return;
        setProduct(res.product);
        setWarehouseInventory(res.warehouses || []);
        setLoading(false);
      })
      .catch((err) => {
        if (!active) return;
        console.error("Failed to load product stock details:", err);
        setError(t("validation.productNotFound"));
        setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [productId, t]);

  useEffect(() => {
    if (activeTab !== "journal") return;
    let active = true;
    setLoadingJournal(true);
    setJournalError("");

    const {
      warehouseLocationId,
      movementType,
      registerType = "All",
      startDate,
      endDate,
    } = journalFilters;
    const apiFilters = {
      productId,
      warehouseLocationId,
      movementType,
      startDate,
      endDate,
    };
    const needsRegisterFilter = Boolean(registerType && registerType !== "All");

    const loadJournal = needsRegisterFilter
      ? collectPageRecords(
          (page, size) =>
            inventoryManagementService.getStockJournal({
              ...apiFilters,
              page,
              size,
            }),
          JOURNAL_REGISTER_FETCH_SIZE
        ).then((rows) => {
          const filtered = filterJournalByRegisterType(rows, registerType);
          const start = (journalPage - 1) * JOURNAL_PAGE_SIZE;
          return {
            content: filtered.slice(start, start + JOURNAL_PAGE_SIZE),
            totalElements: filtered.length,
          };
        })
      : inventoryManagementService.getStockJournal({
          ...apiFilters,
          page: journalPage - 1,
          size: JOURNAL_PAGE_SIZE,
        });

    loadJournal
      .then((res) => {
        if (!active) return;
        setJournalData(res.content || []);
        setJournalTotalItems(res.totalElements ?? (res.content || []).length);
        setLoadingJournal(false);
      })
      .catch((err) => {
        if (!active) return;
        console.error("Failed to filter product journal:", err);
        setJournalData([]);
        setJournalTotalItems(0);
        setJournalError(t("validation.failedToRetrieveStockJournal"));
        setLoadingJournal(false);
      });

    return () => {
      active = false;
    };
  }, [activeTab, productId, journalFilters, journalPage, t]);

  const toggleWarehouseExpand = (whId) => {
    setExpandedWarehouses((prev) => ({
      ...prev,
      [whId]: !prev[whId],
    }));
  };

  const handleApplyJournalFilters = useCallback((nextFilters) => {
    setJournalPage(1);
    setJournalFilters({
      warehouseLocationId: nextFilters.warehouseLocationId || "All",
      movementType: nextFilters.movementType || "All",
      registerType: nextFilters.registerType || "All",
      startDate: nextFilters.startDate || "",
      endDate: nextFilters.endDate || "",
    });
  }, []);

  const handleResetFilters = useCallback(() => {
    setJournalPage(1);
    setJournalFilters({
      warehouseLocationId: "All",
      movementType: "All",
      registerType: "All",
      startDate: "",
      endDate: "",
    });
  }, []);

  const warehouseOptions = useMemo(() => {
    return warehouseInventory
      .filter((w) => w.warehouseId && w.warehouseName)
      .map((w) => ({ value: String(w.warehouseId), label: w.warehouseName }));
  }, [warehouseInventory]);

  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (journalFilters.warehouseLocationId !== "All") count++;
    if (journalFilters.movementType !== "All") count++;
    if (journalFilters.registerType && journalFilters.registerType !== "All") {
      count++;
    }
    if (journalFilters.startDate) count++;
    if (journalFilters.endDate) count++;
    return count;
  }, [journalFilters]);

  const valueKpis = useMemo(() => {
    return warehouseInventory.reduce(
      (acc, wh) => {
        acc.totalStockValue += Number(wh.stockValue) || 0;
        acc.availableStockValue += Number(wh.availableStockValue) || 0;
        acc.defectiveValue += Number(wh.defectiveValue) || 0;
        return acc;
      },
      { totalStockValue: 0, availableStockValue: 0, defectiveValue: 0 }
    );
  }, [warehouseInventory]);

  return (
    <AppLayout
      breadcrumb={[
        { label: t("inventory.operations") },
        {
          label: t("inventory.inventoryManagement"),
          onClick: () => navigate("/inventory-management/stock-position"),
        },
        { label: product?.productName || t("inventory.stockPositionDetail") },
      ]}
      className="space-y-6"
    >
      <div className="space-y-4 font-sans">
        {loading ? (
          <div className="space-y-4 animate-pulse">
            <div className="h-8 bg-gray-100 rounded w-2/3 max-w-md" />
            <div className="h-4 bg-gray-50 rounded w-1/2 max-w-sm" />
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
              <div className="h-[88px] rounded-xl bg-white border border-(--color-text-border)" />
              <div className="h-[88px] rounded-xl bg-white border border-(--color-text-border)" />
              <div className="h-[88px] rounded-xl bg-white border border-(--color-text-border)" />
              <div className="h-[88px] rounded-xl bg-white border border-(--color-text-border)" />
            </div>
          </div>
        ) : error || !product ? (
          <>
            <PageTitleRow
              back={
                <PageBackLink
                  to="/inventory-management/stock-position"
                  label={t("formActions.back")}
                />
              }
            >
              <h1 className={cn(ui.pageTitle)}>
                {t("inventory.stockPositionDetail")}
              </h1>
            </PageTitleRow>
            <div className="p-8 text-center text-error font-semibold text-sm border bg-white rounded-2xl shadow-xs">
              ⚠️ {error || t("validation.productNotFound")}
            </div>
          </>
        ) : (
          <>
            <div className="space-y-1">
              <PageTitleRow
                back={
                  <PageBackLink
                    to="/inventory-management/stock-position"
                    label={t("formActions.back")}
                  />
                }
              >
                <h1 className={cn(ui.pageTitle)}>{product.productName}</h1>
              </PageTitleRow>
              {product.partNumber ? (
                <p className="text-sm per-file-hint font-mono font-semibold">
                  {product.partNumber}
                </p>
              ) : null}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
              <div className="rounded-xl border border-(--color-text-border) bg-white px-4 py-3.5 shadow-xs flex flex-col justify-between min-h-[88px]">
                <div>
                  <span className="text-[10px] font-bold uppercase tracking-wider per-file-hint">
                    {t("inventory.totalStockValue")}
                  </span>
                  <span className="text-xl sm:text-2xl font-extrabold text-foreground block mt-1 font-mono">
                    {formatCurrency(valueKpis.totalStockValue)}
                  </span>
                </div>
              </div>

              <div className="rounded-xl border border-(--color-text-border) bg-white px-4 py-3.5 shadow-xs flex flex-col justify-between min-h-[88px]">
                <div>
                  <span className="text-[10px] font-bold uppercase tracking-wider per-file-hint">
                    {t("inventory.availableStockValue")}
                  </span>
                  <span className="text-xl sm:text-2xl font-extrabold text-foreground block mt-1 font-mono">
                    {formatCurrency(valueKpis.availableStockValue)}
                  </span>
                </div>
              </div>

              <div className="rounded-xl border border-(--color-text-border) bg-white px-4 py-3.5 shadow-xs flex flex-col justify-between min-h-[88px]">
                <div>
                  <span className="text-[10px] font-bold uppercase tracking-wider per-file-hint">
                    {t("inventory.defectiveValue")}
                  </span>
                  <span className="text-xl sm:text-2xl font-extrabold text-foreground block mt-1 font-mono">
                    {formatCurrency(valueKpis.defectiveValue)}
                  </span>
                </div>
              </div>

              <div className="rounded-xl border border-(--color-text-border) bg-white px-4 py-3.5 shadow-xs flex flex-col justify-between min-h-[88px]">
                <div>
                  <span className="text-[10px] font-bold uppercase tracking-wider per-file-hint">
                    {t("inventory.safetyStock")}
                  </span>
                  <span className="text-xl sm:text-2xl font-extrabold text-foreground block mt-1 font-mono">
                    {formatQty(
                      warehouseInventory.reduce(
                        (sum, wh) => sum + (Number(wh.salesSafetyStock) || 0),
                        0
                      )
                    )}
                  </span>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {!loading && !error && product ? (
        <div className="space-y-6 font-sans">
          <div className="flex border-b border-(--color-text-border) font-sans">
            <button
              onClick={() => setActiveTab("position")}
              className={cn(
                "px-5 py-2.5 font-bold text-xs border-b-2 tracking-wider uppercase transition cursor-pointer focus:outline-hidden",
                activeTab === "position"
                  ? "border-primary text-primary bg-transparent"
                  : "border-transparent per-file-hint hover:text-foreground"
              )}
            >
              {t("inventory.stockPosition")}
            </button>
            <button
              onClick={() => setActiveTab("journal")}
              className={cn(
                "px-5 py-2.5 font-bold text-xs border-b-2 tracking-wider uppercase transition cursor-pointer focus:outline-hidden",
                activeTab === "journal"
                  ? "border-primary text-primary bg-transparent"
                  : "border-transparent per-file-hint hover:text-foreground"
              )}
            >
              {t("inventory.journal")}
            </button>
          </div>

          {activeTab === "position" && (
            <ContentCard bodyClassName="overflow-hidden">
              {warehouseInventory.length === 0 ? (
                <div className="px-4 py-8 text-center text-gray-400 font-semibold text-sm">
                  {t("inventory.noWarehouseRecords")}
                </div>
              ) : (
                <>
                  {/* Desktop table */}
                  <div className="hidden md:block overflow-x-auto">
                    <ClientTable className="w-full text-sm table-fixed min-w-[800px] border-collapse font-sans">
                      <colgroup>
                        <col className="w-[200px]" />
                        <col className="w-[70px]" />
                        <col className="w-[80px]" />
                        <col className="w-[90px]" />
                        <col className="w-[90px]" />
                        <col className="w-[80px]" />
                        <col className="w-[90px] bg-[#EBF2FC]" />
                        <col className="w-[100px]" />
                        <col className="w-[50px]" />
                      </colgroup>
                      <thead>
                        <tr className="text-[10px] uppercase tracking-wider per-file-hint bg-workspace/30 border-b border-(--color-text-border)/65 font-bold">
                          <StackedHeader label={t("inventory.warehouse")} />
                          <StackedHeader label={t("inventory.total")} align="right" />
                          <StackedHeader label={t("status.reserved")} align="right" />
                          <StackedHeader label={t("inventory.serviceReserved")} align="right" />
                          <StackedHeader label={t("inventory.safetyStock")} align="right" />
                          <StackedHeader label={t("inventory.defective")} align="right" />
                          <StackedHeader
                            label={t("status.available")}
                            align="right"
                            className="bg-[#EBF2FC] text-primary font-extrabold"
                          />
                          <StackedHeader label={t("inventory.stockValue")} align="right" />
                          <th className="px-3 py-2 text-center" />
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 font-medium">
                        {warehouseInventory.map((wh) => {
                          const isExpanded = !!expandedWarehouses[wh.warehouseId];
                          return (
                            <React.Fragment key={wh.warehouseId}>
                              <tr
                                onClick={() => toggleWarehouseExpand(wh.warehouseId)}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    toggleWarehouseExpand(wh.warehouseId);
                                  }
                                }}
                                role="button"
                                tabIndex={0}
                                aria-expanded={isExpanded}
                                className="hover:bg-gray-50/70 transition-colors cursor-pointer"
                              >
                                <td className="px-4 py-3.5 font-bold text-foreground truncate">
                                  {wh.warehouseName}
                                </td>
                                <td className="px-4 py-3.5 text-right font-semibold text-foreground font-mono">
                                  {formatQty(wh.totalStock)}
                                </td>
                                <td className="px-4 py-3.5 text-right per-file-hint font-mono">
                                  {wh.reservedStock > 0 ? formatQty(wh.reservedStock) : "—"}
                                </td>
                                <td className="px-4 py-3.5 text-right per-file-hint font-mono">
                                  {(Number(wh.serviceSafetyStock ?? wh.maintenanceReserve) || 0) > 0
                                    ? formatQty(wh.serviceSafetyStock ?? wh.maintenanceReserve)
                                    : "—"}
                                </td>
                                <td className="px-4 py-3.5 text-right per-file-hint font-mono">
                                  {(Number(wh.salesSafetyStock) || 0) > 0
                                    ? formatQty(wh.salesSafetyStock)
                                    : "—"}
                                </td>
                                <td className="px-4 py-3.5 text-right text-rose-600 font-mono">
                                  {wh.defectiveStock > 0 ? formatQty(wh.defectiveStock) : "—"}
                                </td>
                                <td className="px-4 py-3.5 text-right font-extrabold text-primary bg-[#EBF2FC] font-mono">
                                  {formatQty(wh.availableStock)}
                                </td>
                                <td className="px-4 py-3.5 text-right font-bold text-foreground font-mono">
                                  {formatCurrency(wh.stockValue)}
                                </td>
                                <td className="px-4 py-3.5 text-center per-file-hint">
                                  {isExpanded ? (
                                    <ChevronUp className="h-4 w-4 mx-auto" aria-hidden />
                                  ) : (
                                    <ChevronDown className="h-4 w-4 mx-auto" aria-hidden />
                                  )}
                                </td>
                              </tr>

                              {isExpanded && (
                                <tr>
                                  <td
                                    colSpan="9"
                                    className="bg-gray-50/50 p-4 border-t border-b border-gray-100"
                                  >
                                    <div className="space-y-2 pl-4">
                                      <span className="text-[10px] font-extrabold uppercase tracking-wider text-primary block">
                                        {t("inventory.batchLevelBreakdown")}
                                      </span>
                                      <div className="border border-gray-150 rounded-xl overflow-hidden bg-white shadow-xs overflow-x-auto">
                                        <ClientTable className="w-full text-xs table-fixed min-w-[940px] border-collapse">
                                          <colgroup>
                                            <col className="w-[120px]" />
                                            <col className="w-[110px]" />
                                            <col className="w-[110px]" />
                                            <col className="w-[120px]" />
                                            <col className="w-[90px]" />
                                            <col className="w-[90px]" />
                                            <col className="w-[90px]" />
                                            <col className="w-[90px]" />
                                          </colgroup>
                                          <thead>
                                            <tr className="bg-gray-100/70 border-b border-gray-150 text-[10px] uppercase tracking-wide text-gray-400 font-bold">
                                              <th className="px-3 py-2 text-left">
                                                {t("inventory.batchNumber")}
                                              </th>
                                              <th className="px-3 py-2 text-left">
                                                {t("dateTime.receiptDate")}
                                              </th>
                                              <th className="px-3 py-2 text-left">
                                                {t("inventory.warrantyEnd")}
                                              </th>
                                              <th className="px-3 py-2 text-right">
                                                {t("inventory.purchasePricePerUnit")}
                                              </th>
                                              <th className="px-3 py-2 text-right">
                                                {t("inventory.acceptedQuantity")}
                                              </th>
                                              <th className="px-3 py-2 text-right">
                                                {t("status.reserved")}
                                              </th>
                                              <th className="px-3 py-2 text-right">
                                                {t("inventory.defective")}
                                              </th>
                                              <th className="px-3 py-2 text-right">
                                                {t("status.available")}
                                              </th>
                                            </tr>
                                          </thead>
                                          <tbody className="divide-y divide-gray-100 font-medium">
                                            {!wh.batches || wh.batches.length === 0 ? (
                                              <tr>
                                                <td
                                                  colSpan="8"
                                                  className="px-3 py-4 text-center text-gray-400 font-semibold"
                                                >
                                                  {t("inventory.noStockRecordsFound")}
                                                </td>
                                              </tr>
                                            ) : (
                                              wh.batches.map((b) => (
                                                <tr
                                                  key={`${b.batchNumber}-${b.receiptDate}-${b.purchasePricePerUnit}`}
                                                  className="hover:bg-gray-50/40"
                                                >
                                                  <td className="px-3 py-2 font-mono font-bold text-foreground">
                                                    {b.batchNumber}
                                                  </td>
                                                  <td className="px-3 py-2 text-gray-500 font-semibold">
                                                    {b.receiptDate
                                                      ? new Date(
                                                          b.receiptDate
                                                        ).toLocaleDateString("en-IN", {
                                                          day: "numeric",
                                                          month: "short",
                                                          year: "numeric",
                                                        })
                                                      : "—"}
                                                  </td>
                                                  <td className="px-3 py-2 text-gray-500 font-semibold">
                                                    {b.warrantyEnd
                                                      ? new Date(
                                                          b.warrantyEnd
                                                        ).toLocaleDateString("en-IN", {
                                                          day: "numeric",
                                                          month: "short",
                                                          year: "numeric",
                                                        })
                                                      : "—"}
                                                  </td>
                                                  <td className="px-3 py-2 text-right font-mono text-foreground font-semibold">
                                                    {formatCurrency(b.purchasePricePerUnit)}
                                                  </td>
                                                  <td className="px-3 py-2 text-right font-mono text-foreground font-semibold">
                                                    {formatQty(b.acceptedQuantity)}
                                                  </td>
                                                  <td className="px-3 py-2 text-right font-mono text-gray-500">
                                                    {b.reservedQty > 0
                                                      ? formatQty(b.reservedQty)
                                                      : "—"}
                                                  </td>
                                                  <td className="px-3 py-2 text-right font-mono text-rose-500">
                                                    {b.defectiveQty > 0
                                                      ? formatQty(b.defectiveQty)
                                                      : "—"}
                                                  </td>
                                                  <td className="px-3 py-2 text-right font-mono font-bold text-emerald-600">
                                                    {formatQty(b.availableStock ?? b.availableQty)}
                                                  </td>
                                                </tr>
                                              ))
                                            )}
                                          </tbody>
                                        </ClientTable>
                                      </div>
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </ClientTable>
                  </div>

                  {/* Mobile warehouse cards — whole card toggles batch breakdown */}
                  <div className="md:hidden space-y-3 p-3">
                    {warehouseInventory.map((wh) => {
                      const isExpanded = !!expandedWarehouses[wh.warehouseId];
                      return (
                        <div
                          key={wh.warehouseId}
                          className="rounded-xl border border-(--color-text-border)/60 bg-white shadow-xs overflow-hidden"
                        >
                          <button
                            type="button"
                            onClick={() => toggleWarehouseExpand(wh.warehouseId)}
                            aria-expanded={isExpanded}
                            className="w-full text-left px-3.5 py-3.5 space-y-2.5 active:bg-workspace/40 cursor-pointer"
                          >
                            <div className="flex items-start justify-between gap-3">
                              <p className="text-sm font-bold text-foreground truncate min-w-0">
                                {wh.warehouseName}
                              </p>
                              {isExpanded ? (
                                <ChevronUp className="h-4 w-4 shrink-0 per-file-hint" />
                              ) : (
                                <ChevronDown className="h-4 w-4 shrink-0 per-file-hint" />
                              )}
                            </div>
                            <div className="space-y-1.5 text-xs">
                              <div className="flex justify-between gap-3">
                                <span className="per-file-hint">
                                  {t("inventory.total")}
                                </span>
                                <span className="font-semibold font-mono">
                                  {formatQty(wh.totalStock)}
                                </span>
                              </div>
                              <div className="flex justify-between gap-3">
                                <span className="per-file-hint">
                                  {t("status.reserved")}
                                </span>
                                <span className="font-semibold font-mono">
                                  {wh.reservedStock > 0 ? formatQty(wh.reservedStock) : "—"}
                                </span>
                              </div>
                              <div className="flex justify-between gap-3">
                                <span className="per-file-hint">
                                  {t("inventory.serviceReserved")}
                                </span>
                                <span className="font-semibold font-mono">
                                  {(Number(wh.serviceSafetyStock ?? wh.maintenanceReserve) || 0) > 0
                                    ? formatQty(wh.serviceSafetyStock ?? wh.maintenanceReserve)
                                    : "—"}
                                </span>
                              </div>
                              <div className="flex justify-between gap-3">
                                <span className="per-file-hint">
                                  {t("inventory.safetyStock")}
                                </span>
                                <span className="font-semibold font-mono">
                                  {(Number(wh.salesSafetyStock) || 0) > 0
                                    ? formatQty(wh.salesSafetyStock)
                                    : "—"}
                                </span>
                              </div>
                              <div className="flex justify-between gap-3">
                                <span className="per-file-hint">
                                  {t("inventory.defective")}
                                </span>
                                <span className="font-semibold font-mono text-rose-600">
                                  {wh.defectiveStock > 0 ? formatQty(wh.defectiveStock) : "—"}
                                </span>
                              </div>
                              <div className="flex justify-between gap-3">
                                <span className="per-file-hint">
                                  {t("status.available")}
                                </span>
                                <span className="font-extrabold font-mono text-primary">
                                  {formatQty(wh.availableStock)}
                                </span>
                              </div>
                              <div className="flex justify-between gap-3">
                                <span className="per-file-hint">
                                  {t("inventory.stockValue")}
                                </span>
                                <span className="font-bold font-mono">
                                  {formatCurrency(wh.stockValue)}
                                </span>
                              </div>
                            </div>
                          </button>

                          {isExpanded && (
                            <div className="px-4 pb-4 space-y-2 bg-workspace/30 border-t border-(--color-text-border)/25">
                              <span className="text-[10px] font-extrabold uppercase tracking-wider text-primary block pt-3">
                                {t("inventory.batchLevelBreakdown")}
                              </span>
                              {!wh.batches || wh.batches.length === 0 ? (
                                <p className="text-xs text-center text-gray-400 font-semibold py-3">
                                  {t("inventory.noStockRecordsFound")}
                                </p>
                              ) : (
                                <ul className="space-y-2">
                                  {wh.batches.map((b) => (
                                    <li
                                      key={`${b.batchNumber}-${b.receiptDate}-${b.purchasePricePerUnit}`}
                                      className="rounded-xl border border-(--color-text-border)/50 bg-white p-3 space-y-1.5 text-xs shadow-xs"
                                    >
                                      <div className="flex justify-between gap-2">
                                        <span className="font-mono font-bold text-foreground">
                                          {b.batchNumber}
                                        </span>
                                        <span className="font-extrabold font-mono text-emerald-600">
                                          {formatQty(b.availableStock ?? b.availableQty)}{" "}
                                          <span className="font-semibold per-file-hint">
                                            {t("status.available")}
                                          </span>
                                        </span>
                                      </div>
                                      <div className="flex justify-between gap-2 per-file-hint">
                                        <span>{t("dateTime.receiptDate")}</span>
                                        <span className="font-semibold text-foreground">
                                          {b.receiptDate
                                            ? new Date(b.receiptDate).toLocaleDateString(
                                                "en-IN",
                                                {
                                                  day: "numeric",
                                                  month: "short",
                                                  year: "numeric",
                                                }
                                              )
                                            : "—"}
                                        </span>
                                      </div>
                                      <div className="flex justify-between gap-2 per-file-hint">
                                        <span>{t("inventory.warrantyEnd")}</span>
                                        <span className="font-semibold text-foreground">
                                          {b.warrantyEnd
                                            ? new Date(b.warrantyEnd).toLocaleDateString(
                                                "en-IN",
                                                {
                                                  day: "numeric",
                                                  month: "short",
                                                  year: "numeric",
                                                }
                                              )
                                            : "—"}
                                        </span>
                                      </div>
                                      <div className="flex justify-between gap-2 per-file-hint">
                                        <span>{t("inventory.purchasePricePerUnit")}</span>
                                        <span className="font-semibold font-mono text-foreground">
                                          {formatCurrency(b.purchasePricePerUnit)}
                                        </span>
                                      </div>
                                      <div className="flex justify-between gap-2 per-file-hint">
                                        <span>{t("inventory.acceptedQuantity")}</span>
                                        <span className="font-semibold font-mono text-foreground">
                                          {formatQty(b.acceptedQuantity)}
                                        </span>
                                      </div>
                                      <div className="flex justify-between gap-2 per-file-hint">
                                        <span>{t("status.reserved")}</span>
                                        <span className="font-semibold font-mono">
                                          {b.reservedQty > 0 ? formatQty(b.reservedQty) : "—"}
                                        </span>
                                      </div>
                                      <div className="flex justify-between gap-2 per-file-hint">
                                        <span>{t("inventory.defective")}</span>
                                        <span className="font-semibold font-mono text-rose-500">
                                          {b.defectiveQty > 0 ? formatQty(b.defectiveQty) : "—"}
                                        </span>
                                      </div>
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </ContentCard>
          )}

          {activeTab === "journal" && (
            <div className="space-y-6">
              <ContentCard className="overflow-hidden">
                <div className="px-4 sm:px-6 py-3 border-b border-(--color-text-border)/70 flex flex-wrap items-center justify-between gap-3 bg-white font-sans">
                  <span className="text-xs font-bold text-foreground">
                    {t("inventory.stockJournalLogs")}
                  </span>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setJournalFilterModalOpen(true)}
                      className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-(--color-text-border) bg-white text-xs font-bold text-foreground hover:bg-workspace/40 hover:text-primary transition cursor-pointer"
                    >
                      <Funnel className="h-3.5 w-3.5" />
                      <span>{t("formActions.filter")}</span>
                      {activeFiltersCount > 0 && (
                        <span className="inline-flex h-4.5 min-w-4.5 items-center justify-center rounded-full bg-primary px-1 text-[9px] font-extrabold text-white font-mono leading-none">
                          {activeFiltersCount}
                        </span>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={handleResetFilters}
                      disabled={activeFiltersCount === 0}
                      className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-(--color-text-border) bg-white text-xs font-bold text-foreground hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition cursor-pointer"
                    >
                      <RefreshCw className="h-3.5 w-3.5 per-file-hint" />
                      <span>{t("formActions.resetFilters")}</span>
                    </button>
                  </div>
                </div>

                {loadingJournal ? (
                  <div className="p-6 space-y-4 animate-pulse">
                    <div className="h-10 bg-gray-100 rounded w-full" />
                    <div className="h-10 bg-gray-50 rounded w-full" />
                  </div>
                ) : journalError ? (
                  <div className="p-8 text-center text-error font-semibold text-sm">
                    ⚠️ {journalError}
                  </div>
                ) : journalData.length === 0 ? (
                  <div className="p-12 flex flex-col items-center text-center space-y-3 font-sans max-w-sm mx-auto">
                    <div className="h-10 w-10 rounded-full bg-workspace per-file-hint grid place-items-center">
                      <Funnel className="h-4.5 w-4.5" />
                    </div>
                    <div>
                      <h5 className="text-xs font-bold text-foreground">
                        {t("inventory.noMovementsFound")}
                      </h5>
                      <p className="text-[11px] per-file-hint mt-1.5 leading-normal">
                        {t("inventory.noMovementsFoundHint")}
                      </p>
                    </div>
                    {activeFiltersCount > 0 && (
                      <button
                        type="button"
                        onClick={handleResetFilters}
                        className="inline-flex h-8 px-4 rounded-lg bg-primary text-white text-[11px] font-bold hover:bg-accent transition cursor-pointer"
                      >
                        {t("formActions.resetFilters")}
                      </button>
                    )}
                  </div>
                ) : (
                  <InventoryJournalTable
                    data={journalData}
                    page={journalPage}
                    pageSize={JOURNAL_PAGE_SIZE}
                    totalItems={journalTotalItems}
                    onPageChange={setJournalPage}
                  />
                )}
              </ContentCard>

              <StockJournalFiltersModal
                open={journalFilterModalOpen}
                onOpenChange={setJournalFilterModalOpen}
                appliedFilters={journalFilters}
                onApply={handleApplyJournalFilters}
                warehouseOptions={warehouseOptions}
                showProductFilter={false}
              />
            </div>
          )}
        </div>
      ) : null}
    </AppLayout>
  );
}
